// Port of Flutter's roomsScreen.dart.
//
//   AppBar   avatar · location name · "<Name> <Surname>"
//   body     common-devices strip, the sensor strip, then the room list
//   room row ButtonColor card (elevation 4, radius 12) with a location pin,
//            the room name, an "Add: <ADD1>" subtitle and an ↑ chevron
//   FAB      Add Common Device · Add Room
//
// Rooms and common devices are drawn in the customer's own `SortOrder` — the
// desktop shell always did and this one did not, which is how one house came to
// have two orders. See core/utils/sortOrder.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowUpwardSharpIcon from '@mui/icons-material/ArrowUpwardSharp';
import AddIcon from '@mui/icons-material/Add';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import SensorStrip from '../components/SensorStrip';
import CircleMenu from '../components/CircleMenu';
import AddRoomDialog from '../components/AddRoomDialog';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import { useApp } from '../../../core/state/AppContext';
import { getRooms, getCommonDevices } from '../../../core/api/api';
import { sortByOrder } from '../../../core/utils/sortOrder';
import { splitSensors } from '../../../core/devices/sensorState';
import { drillFromRooms, arrivedViaBack, parentOf } from '../../../core/nav/appNav';

export default function MobileRoomsScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { state } = useLocation();
  const {
    user, userId, locations, toggleFavourite, canUpdateFirmware,
  } = useApp();

  const [rooms, setRooms] = useState(null);   // null = still loading
  const [common, setCommon] = useState([]);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const redirectedRef = useRef(false);

  const locationName = state?.locationName
    || locations.find((l) => String(l.LocationID) === String(locationId))?.LocationName
    || 'Location';

  const load = useCallback(async () => {
    if (!userId || !locationId) return;
    const [rs, cs] = await Promise.all([
      getRooms(userId, locationId).catch(() => null),
      getCommonDevices(userId, locationId).catch(() => null),
    ]);
    setCommon(sortByOrder(Array.isArray(cs?.data) ? cs.data : []));
    setRooms(sortByOrder(Array.isArray(rs?.data) ? rs.data : []));
  }, [userId, locationId]);

  useEffect(() => { load(); }, [load]);

  // A location's roomless sensors get the same row they get inside a room —
  // state on the card, not behind a tap. See core/devices/sensorState.
  const { sensors, controls } = useMemo(() => splitSensors(common), [common]);

  // Flutter's _initialRender, widened: a location with exactly ONE thing to tap
  // drops straight into it, so the user isn't asked to choose from a list of
  // one. Both lists count — one room and one common device is a choice, and a
  // lone common device is opened directly because it lives in no room.
  //
  // ⚠️ NOT WHEN THE USER ARRIVED BY GOING BACK. This screen is the only way to
  // Add Room, Add Common Device and the location-wide Update Code; drilling
  // forward again on a back would put all three permanently out of reach for a
  // single-room site, and the user could never leave the device. See
  // core/nav/appNav.js.
  //
  // ⚠️ `rooms == null` MEANS STILL LOADING, and `common` starts as []. Deciding
  // before both have landed reads a one-room location as having nothing at all.
  useEffect(() => {
    if (rooms == null || redirectedRef.current || arrivedViaBack(state)) return;
    const target = drillFromRooms({
      rooms, common, locationId, locationName,
    });
    if (!target) return;
    redirectedRef.current = true;
    navigate(target.path, { replace: true, state: target.state });
  }, [rooms, common, state, navigate, locationName, locationId]);

  // `room` is passed for a device listed under one, and omitted for the common
  // strip — those belong to the LOCATION, so back should land here, not on a
  // room device list they are not in. See core/devices/deviceNav.js.
  const openDevice = (d, room) => navigate(`/device/${d.UniqueNumber}`, {
    state: {
      device: d,
      from: {
        locationId,
        locationName,
        roomId: room?.RoomID ?? null,
        roomName: room?.RoomName ?? '',
      },
    },
  });

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      {/* The arrow and the Android back button must give the same answer, so
          both go through parentOf. History would send a drilled-into location
          back out of the app, because the drill replaced the entry. */}
      <ProfileTitleBar
        title={locationName}
        subtitle={`${user?.Name || ''} ${user?.Surname || ''}`.trim()}
        onBack={() => {
          const t = parentOf(`/rooms/${locationId}`, state);
          if (t) navigate(t.path, { state: t.state });
          else navigate(-1);
        }}
      />

      {/* PINNED. Two independent scroll areas, as in the Dart (a fixed row above
          an Expanded list): common devices stay on screen and scroll sideways in
          their own container, while only the room list scrolls vertically. */}
      <CommonDevicesStrip
        devices={controls}
        onOpen={openDevice}
        onToggleFavourite={async (d, fav) => { await toggleFavourite(d, fav); load(); }}
      />

      <SensorStrip
        devices={sensors}
        onOpen={openDevice}
        onToggleFavourite={async (d, fav) => { await toggleFavourite(d, fav); load(); }}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {rooms == null ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography sx={{ color: c.text, fontSize: 20 }}>Loading...</Typography>
          </Box>
        ) : (
          rooms.map((r) => (
            <Card
              key={r.RoomID}
              elevation={4}
              sx={{ bgcolor: c.button, borderRadius: '12px', mx: 2, my: 1 }}
            >
              <ListItemButton
                sx={{ p: 1.5, borderRadius: '12px' }}
                onClick={() => navigate(`/room/${r.RoomID}/devices`, {
                  state: { roomName: r.RoomName, locationName, locationId },
                })}
              >
                <ListItemIcon sx={{ color: c.text, minWidth: 40 }}>
                  <LocationOnIcon />
                </ListItemIcon>
                <ListItemText
                  primary={r.RoomName || 'No Room Name'}
                  secondary={`Add: ${r.ADD1 ?? 'Unknown'}`}
                  primaryTypographyProps={{ sx: { color: c.text, fontSize: 15, fontWeight: 700 } }}
                  secondaryTypographyProps={{ sx: { color: c.text, fontSize: 14 } }}
                />
                <ArrowUpwardSharpIcon sx={{ color: c.text }} />
              </ListItemButton>
            </Card>
          ))
        )}
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Common Device',
            onClick: () => navigate(`/add-device?locationId=${locationId}&roomId=0`),
          },
          {
            icon: <MeetingRoomIcon />,
            title: 'Add Room',
            onClick: () => setAddRoomOpen(true),
          },
          // Location-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Location)',
            onClick: () => setUpdateOpen(true),
          }] : []),
        ]}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'location', id: locationId, name: locationName }}
        onClose={() => setUpdateOpen(false)}
      />

      <AddRoomDialog
        open={addRoomOpen}
        locationId={locationId}
        onClose={() => setAddRoomOpen(false)}
        onAdded={() => { setAddRoomOpen(false); load(); }}
      />
    </Box>
  );
}
